import React, { Fragment, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Bars3BottomLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import useModal from '../../hooks/useModal';
import LogoMark from '../../images/logo-mark.png';
import { classNames } from '../helpers/functions';

const Shell = ({
	UserMenu,
	navigation = [],
	secondaryNavigation = [],
	secondaryNavigationTitle = '',
	children,
	settingsNavigation,
}) => {
	const navigate = useNavigate();
	const searchBar = useRef();

	const { setOpen } = useModal('tabletView');
	const { setOpen: openGlobalSearch } = useModal('globalSearch');

	const [sidebarOpen, setSidebarOpen] = useState(false);
	const onClickLink = (path) => {
		if (path) {
			navigate(path);
		}
		setSidebarOpen(false);
	};

	const user = localStorage.getItem('user');
	const userJSON = user ? JSON.parse(user) : null;
	const iPadView = userJSON?.organization?.flags?.indexOf('iPadView') > -1;

	const PrimaryNav = () =>
		navigation.map((item, i) => {
			const current = item.path === history.location.pathname;

			return (
				<div key={`nav-item-${i}`}>
					<a
						key={`${item.name}-${i}`}
						href={item.href}
						onClick={() => onClickLink(item.path)}
						className={classNames(
							current
								? 'bg-gray-700 text-white'
								: 'text-gray-300 hover:bg-gray-700 hover:text-white',
							'group w-full flex items-center pl-2 py-2 text-xs font-medium rounded-md cursor-pointer'
						)}
					>
						{item.icon && (
							<item.icon
								className={classNames(
									current
										? 'text-gray-300'
										: 'text-gray-400 group-hover:text-gray-300',
									'mr-4 flex-shrink-0 h-5 w-5'
								)}
								aria-hidden="true"
							/>
						)}
						{item.name}
					</a>
					{item?.children?.length
						? item?.children?.map((child, i) => <a key={i}>{child?.name}</a>)
						: null}
				</div>
			);
		});

	const SecondaryNav = () => (
		<div className="space-y-1">
			{secondaryNavigationTitle && (
				<h3
					className="px-3 my-3 text-sm font-medium text-gray-500"
					id="projects-headline"
				>
					{secondaryNavigationTitle}
				</h3>
			)}
			<div
				className="space-y-1"
				role="group"
				aria-labelledby="projects-headline"
			>
				{[
					...secondaryNavigation,
					...(iPadView
						? [{ name: 'iPad Mode', onClick: () => setOpen() }]
						: []),
				].map((item) => (
					<a
						key={item.name}
						href={item.href}
						onClick={() =>
							typeof item?.onClick === 'function'
								? item?.onClick()
								: onClickLink(item.path)
						}
						className="truncategroup flex items-center rounded-md px-3 py-2 text-xs font-medium text-gray-400 hover:bg-gray-700 hover:text-gray-300 cursor-pointer"
					>
						{item.icon && (
							<item.icon
								className={classNames(
									'text-gray-400 group-hover:text-gray-300',
									'mr-4 flex-shrink-0 h-6 w-6'
								)}
								aria-hidden="true"
							/>
						)}
						{item.name}
					</a>
				))}
			</div>
		</div>
	);

	return (
		<>
			<div className="flex sm:h-full">
				<Transition.Root show={sidebarOpen} as={Fragment}>
					<Dialog
						as="div"
						className="relative z-40 md:hidden"
						onClose={setSidebarOpen}
					>
						<Transition.Child
							as={Fragment}
							enter="transition-opacity ease-linear duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="transition-opacity ease-linear duration-300"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<div className="fixed inset-0 bg-gray-600/75" />
						</Transition.Child>

						<div className="fixed inset-0 z-40 flex">
							<Transition.Child
								as={Fragment}
								enter="transition ease-in-out duration-300 transform"
								enterFrom="-translate-x-full"
								enterTo="translate-x-0"
								leave="transition ease-in-out duration-300 transform"
								leaveFrom="translate-x-0"
								leaveTo="-translate-x-full"
							>
								<Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-gray-800 pt-5 pb-4">
									<Transition.Child
										as={Fragment}
										enter="ease-in-out duration-300"
										enterFrom="opacity-0"
										enterTo="opacity-100"
										leave="ease-in-out duration-300"
										leaveFrom="opacity-100"
										leaveTo="opacity-0"
									>
										<div className="absolute top-0 right-0 -mr-12 pt-2">
											<button
												type="button"
												className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
												onClick={() => setSidebarOpen(false)}
											>
												<span className="sr-only">Close sidebar</span>
												<XMarkIcon
													className="h-6 w-6 text-white"
													aria-hidden="true"
												/>
											</button>
										</div>
									</Transition.Child>
									<div
										className="flex flex-shrink-0 items-center px-4 cursor-pointer"
										onClick={() => navigate('/')}
									>
										<img className="h-8 w-auto" src={LogoMark} alt="Scipods" />
									</div>
									<div className="mt-5 h-0 flex-1 overflow-y-auto">
										<nav className="space-y-1 px-2">
											<PrimaryNav />
											<SecondaryNav />
										</nav>
									</div>
								</Dialog.Panel>
							</Transition.Child>
							<div className="w-14 flex-shrink-0" aria-hidden="true">
								{/* Dummy element to force sidebar to shrink to fit close icon */}
							</div>
						</div>
					</Dialog>
				</Transition.Root>

				<div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
					<div className="flex min-h-0 flex-1 flex-col bg-gray-800">
						<div
							className="flex h-16 flex-shrink-0 items-center bg-gray-900 px-4 cursor-pointer"
							onClick={() => navigate('/')}
						>
							<img className="h-8 w-auto" src={LogoMark} alt="Scipods" />
						</div>
						<div className="mt-5 flex flex-grow flex-col">
							<nav className="flex-1 space-y-8 px-2">
								<div className="space-y-1">
									<PrimaryNav />
								</div>
								<SecondaryNav />
							</nav>
							{settingsNavigation ? (
								<div>
									{[settingsNavigation].map((item) => (
										<a
											key={item.name}
											href={item.href}
											onClick={() =>
												typeof item?.onClick === 'function'
													? item?.onClick()
													: onClickLink(item.path)
											}
											className="truncategroup flex items-center rounded-md px-3 py-2 text-xs font-medium text-gray-400 hover:bg-gray-700 hover:text-gray-300 cursor-pointer"
										>
											{item.icon && (
												<item.icon
													className={classNames(
														'text-gray-400 group-hover:text-gray-300',
														'mr-4 flex-shrink-0 h-6 w-6'
													)}
													aria-hidden="true"
												/>
											)}
											{item.name}
										</a>
									))}
								</div>
							) : null}
						</div>
					</div>
				</div>
				<div className="flex flex-1 flex-col lg:pl-64">
					<div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white shadow">
						<button
							type="button"
							className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 lg:hidden"
							onClick={() => setSidebarOpen(true)}
						>
							<span className="sr-only">Open sidebar</span>
							<Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
						</button>
						<div className="flex flex-1 justify-between px-4">
							<div className="flex flex-1">
								<label htmlFor="search-field" className="sr-only">
									Search
								</label>
								<div className="relative w-full text-gray-400 focus-within:text-gray-600">
									<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center">
										<MagnifyingGlassIcon
											className="h-5 w-5"
											aria-hidden="true"
										/>
									</div>
									<input
										ref={searchBar}
										id="search-field"
										className="block h-full w-full border-transparent py-2 pl-8 pr-3 text-gray-900 placeholder-gray-500 focus:border-transparent focus:placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm"
										placeholder="Search"
										type="search"
										name="search"
										autoFocus={false}
										value={''}
										onFocus={(e) => {
											e.target.blur();
										}}
										onClick={() => openGlobalSearch()}
										readOnly
									/>
								</div>
							</div>
							<div className="ml-4 flex items-center lg:ml-6">{UserMenu}</div>
						</div>
					</div>

					<main className="flex flex-1 overflow-hidden">
						<div className="flex flex-1 flex-col overflow-y-auto xl:overflow-hidden">
							<div className="flex flex-1 xl:overflow-scroll">{children}</div>
						</div>
					</main>
				</div>
			</div>
		</>
	);
};

export default Shell;
